import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../Navbar/Navbar";
import Bredcom from "../Bredcom/Bredcom";
import Footer from "../Footer/Footer";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import alumniTestimonials from "../../data/alumniTestimonials.json";

function getInitials(fullName = "") {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  const first = parts[0][0] || "";
  const last = (parts[parts.length - 1][0] || "").trim();
  return (first + last).toUpperCase();
}

function Avatar({ name, src }) {
  const [failed, setFailed] = useState(false);

  const initials = useMemo(() => getInitials(name), [name]);
  const hasImage =
    Boolean(src) && src !== "null" && src !== "undefined" && !failed;

  return (
    <div className="tuition-avatar" aria-label={name}>
      {hasImage ? (
        <img
          src={src}
          alt={name}
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : (
        <span aria-hidden="true">{initials}</span>
      )}
    </div>
  );
}

function Currency({ value }) {
  // simple, consistent formatting (avoid locale surprises in SSR)
  return <>{Number(value).toFixed(0)} €</>;
}

function TuitionPaymentOptions({ price, t }) {
  const paymentRows = [
    {
      key: "oneTime",
      label: t("skolarina.jednokratnoDoOktobra", "jednokratno do 01. oktobra"),
      value: price.oneTime,
      highlight: true,
    },
    {
      key: "semester",
      label: t("skolarina.semestralno", "semestralno"),
      value: price.semester,
      highlight: false,
    },
    {
      key: "installments",
      label: t("skolarina.uratama", "u ratama"),
      value: price.installments,
      highlight: false,
    },
  ];

  return (
    <div className="tuition-price tuition-price--options">
      {paymentRows.map((row) => (
        <div
          className={`tuition-price__option${row.highlight ? " is-featured" : ""}`}
          key={row.key}
        >
          <span className="tuition-price__optionLabel">{row.label}</span>
          <strong className="tuition-price__optionAmount">
            <Currency value={row.value} />
          </strong>
        </div>
      ))}
    </div>
  );
}

function TuitionCard({ p, t, commonPoints, justCompared, onCompare }) {
  return (
    <article className="tuition-card">
      <header className="tuition-card__head">
        <div className="tuition-card__titleRow">
          <h3 className="tuition-card__title">{p.title}</h3>

          {p.badge ? (
            <span className="tuition-badge" aria-label={p.badge}>
              {p.badge}
            </span>
          ) : null}
        </div>

        <p className="tuition-card__lead">{p.lead}</p>

        <div className="tuition-meta">
          <span>
            <i className="fas fa-clock" aria-hidden="true" /> {p.duration}
          </span>
          <span>
            <i className="fas fa-layer-group" aria-hidden="true" /> {p.ects}
          </span>
        </div>
      </header>

      <TuitionPaymentOptions price={p.price} t={t} />

      {p.note ? <div className="tuition-price__note">{p.note}</div> : null}

      <div className="tuition-pointsBlock">
        <div className="tuition-points__label">
          {t("tuitionPage.card.programFocus", "Fokus programa")}
        </div>
        <ul className="tuition-points">
          {p.programPoints.map((x) => (
            <li key={x}>
              <i className="fas fa-check" aria-hidden="true" />
              <span>{x}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="tuition-pointsBlock">
        <div className="tuition-points__label">
          {t("tuitionPage.card.includedAll", "U školarinu je uključeno")}
        </div>
        <ul className="tuition-points">
          {commonPoints.map((x) => (
            <li key={x}>
              <i className="fas fa-check" aria-hidden="true" />
              <span>{x}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="tuition-actions">
        <a
          className="btn tuition-btn tuition-btn--primary"
          href={p.applyUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("skolarina.cta")}{" "}
          <i className="fas fa-arrow-right" aria-hidden="true" />
        </a>

        <Link className="btn tuition-btn tuition-btn--ghost" to={p.href}>
          {t("tuitionPage.cta.more", "Saznaj više")}{" "}
          <i className="fas fa-external-link-alt" aria-hidden="true" />
        </Link>

        <button
          type="button"
          className="btn tuition-btn tuition-btn--text tuition-compareBtn"
          onClick={onCompare}
          aria-label={t(
            "tuitionPage.compare.aria",
            "Tabela za poređenje programa",
          )}
        >
          {justCompared
            ? t("tuitionPage.compare.active", "Upoređuje se")
            : t("tuitionPage.compare.pick", "Uporedi")}
          <i className="fas fa-table" aria-hidden="true" />
        </button>
      </div>
    </article>
  );
}

function CompareTable({ programs, t }) {
  if (!programs?.length) return null;

  return (
    <div
      className="tuition-compare"
      role="region"
      aria-label={t("tuitionPage.compare.aria")}
    >
      <div className="tuition-compare__head">
        <h3 className="tuition-section__title">
          {t("tuitionPage.compare.title")}
        </h3>
        <p className="tuition-section__lead">{t("tuitionPage.compare.lead")}</p>
      </div>

      <div className="tuition-tableWrap">
        <table className="tuition-table">
          <thead>
            <tr>
              <th scope="col">{t("tuitionPage.compare.col")}</th>
              {programs.map((p) => (
                <th scope="col" key={p.key}>
                  <span className="tuition-table__program">{p.title}</span>
                  <span className="tuition-table__mini">
                    {p.duration} • {p.ects}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">{t("tuitionPage.compare.oneTime")}</th>
              {programs.map((p) => (
                <td key={p.key}>
                  <Currency value={p.price.oneTime} />{" "}
                  <span className="tuition-table__muted">
                    / {t("skolarina.jednokratnoDoOktobra")}
                  </span>
                </td>
              ))}
            </tr>

            <tr>
              <th scope="row">{t("tuitionPage.compare.semester")}</th>
              {programs.map((p) => (
                <td key={p.key}>
                  <Currency value={p.price.semester} />{" "}
                  <span className="tuition-table__muted">
                    / {t("skolarina.semestralno")}
                  </span>
                </td>
              ))}
            </tr>

            <tr>
              <th scope="row">{t("tuitionPage.compare.installments")}</th>
              {programs.map((p) => (
                <td key={p.key}>
                  <Currency value={p.price.installments} />{" "}
                  <span className="tuition-table__muted">
                    / {t("skolarina.uratama")}
                  </span>
                </td>
              ))}
            </tr>

            <tr>
              <th scope="row">{t("tuitionPage.compare.focus")}</th>
              {programs.map((p) => (
                <td key={p.key}>
                  <ul className="tuition-table__list">
                    {p.programPoints.slice(0, 3).map((x) => (
                      <li key={x}>{x}</li>
                    ))}
                  </ul>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="tuition-compare__note">
        <i className="fas fa-info-circle" aria-hidden="true" />{" "}
        {t("tuitionPage.compare.note")}
      </div>
    </div>
  );
}

function FaqItem({ q, a }) {
  return (
    <details className="tuition-faq__item">
      <summary>
        <span>{q}</span>
        <i className="fas fa-chevron-down" aria-hidden="true" />
      </summary>
      <div className="tuition-faq__answer">{a}</div>
    </details>
  );
}

function CijeneStudija() {
  const { t, i18n } = useTranslation();

  const lang = (i18n.language || "me").toLowerCase().startsWith("en")
    ? "en"
    : "me";

  const APPLY_URL = useMemo(
    () =>
      t("tuitionPage.applyUrl") ||
      "https://docs.google.com/forms/d/e/1FAIpQLSejqXir_pUuSCp0MwDJLzLoTDiT2SphgLQPO8_-5t35dxh5lw/viewform",
    [t],
  );

  const PRACTICE_PDF = useMemo(
    () =>
      t("tuitionPage.practice.pdfHref") ||
      "/Prilozi/Model%20studentske%20prakse%20i%20prakticne%20nastave%20akademskih%20studija.pdf",
    [t],
  );

  const programs = useMemo(
    () => ({
      undergrad: [
        {
          key: "rfb",
          title: t("skolarina.osnovne1"),
          lead: t("tuitionPage.programs.rfb.lead"),
          href: "/racunovodstvo-finansije-i-bankarstvo",
          duration: t("programi.trajanje3"),
          ects: t("programi.stepenosnovne"),
          price: { oneTime: 1200, semester: 1400, installments: 1500 },
          programPoints: [
            t("tuitionPage.programs.rfb.p1"),
            t("tuitionPage.programs.rfb.p2"),
            t("tuitionPage.programs.rfb.p3"),
          ],
          applyUrl: APPLY_URL,
          badge: t("tuitionPage.badges.undergrad"),
        },
        {
          key: "men",
          title: t("skolarina.osnovne2"),
          lead: t("tuitionPage.programs.men.lead"),
          href: "/menadzment",
          duration: t("programi.trajanje3"),
          ects: t("programi.stepenosnovne"),
          price: { oneTime: 1200, semester: 1400, installments: 1500 },
          programPoints: [
            t("tuitionPage.programs.men.p1"),
            t("tuitionPage.programs.men.p2"),
            t("tuitionPage.programs.men.p3"),
          ],
          applyUrl: APPLY_URL,
          badge: "",
        },
        {
          key: "law-ug",
          title: t("skolarina.osnovne3"),
          lead: t("tuitionPage.programs.lawug.lead"),
          href: "/pravo-osnovne",
          duration: t("programi.trajanje3"),
          ects: t("programi.stepenosnovne"),
          price: { oneTime: 1200, semester: 1400, installments: 1500 },
          programPoints: [
            t("tuitionPage.programs.lawug.p1"),
            t("tuitionPage.programs.lawug.p2"),
            t("tuitionPage.programs.lawug.p3"),
          ],
          applyUrl: APPLY_URL,
          badge: "",
        },
      ],
      master: [
        {
          key: "pe",
          title: t("skolarina.master1"),
          lead: t("pe.opis"),
          href: "/poslovna-ekonomija",
          duration: t("programi.trajanje2"),
          ects: t("programi.stepenmaster"),
          price: { oneTime: 1500, semester: 1700, installments: 1800 },
          programPoints: [
            t("tuitionPage.programs.pe.p1"),
            t("tuitionPage.programs.pe.p2"),
            t("tuitionPage.programs.pe.p3"),
          ],
          applyUrl: APPLY_URL,
          badge: t("tuitionPage.badges.master"),
        },
        {
          key: "ejs",
          title: t("skolarina.master2"),
          lead: t("ejs.opis"),
          href: "/ekonomija-javnog-sektora",
          duration: t("programi.trajanje2"),
          ects: t("programi.stepenmaster"),
          price: { oneTime: 1500, semester: 1700, installments: 1800 },
          programPoints: [
            t("tuitionPage.programs.ejs.p1"),
            t("tuitionPage.programs.ejs.p2"),
            t("tuitionPage.programs.ejs.p3"),
          ],
          applyUrl: APPLY_URL,
          badge: "",
        },
        {
          key: "law-m",
          title: t("skolarina.master3"),
          lead: t("pravoMaster.opis"),
          href: "/pravo-master",
          duration: t("programi.trajanje2"),
          ects: t("programi.stepenmaster"),
          price: { oneTime: 1500, semester: 1700, installments: 1800 },
          programPoints: [
            t("tuitionPage.programs.lawm.p1"),
            t("tuitionPage.programs.lawm.p2"),
            t("tuitionPage.programs.lawm.p3"),
          ],
          applyUrl: APPLY_URL,
          badge: "",
        },
      ],
    }),
    [t, APPLY_URL],
  );

  const commonPoints = useMemo(
    () => [
      t("tuitionPage.includes.textbooksShort", "Udžbenici uključeni"),
      t("tuitionPage.includes.platformShort", "E-platforma + materijali"),
      t("tuitionPage.includes.supportShort", "Podrška i praćenje aktivnosti"),
      t("tuitionPage.includes.materialsShort", "Materijali i resursi"),
      t("tuitionPage.includes.erasmusShort", "Erasmus+ mogućnosti"),
      t("tuitionPage.includes.practiceShort", "Model studentske prakse"),
    ],
    [t],
  );

  const [tab, setTab] = useState("undergrad");
  const [comparePick, setComparePick] = useState("undergrad");
  const [compareNotice, setCompareNotice] = useState(null);

  useEffect(() => {
    // keep compare table in sync with currently viewed level
    setComparePick(tab);
    // reset any old “compared” hint when switching tabs
    setCompareNotice(null);
  }, [tab]);

  useEffect(() => {
    if (!compareNotice) return;
    const timer = setTimeout(() => setCompareNotice(null), 3600);
    return () => clearTimeout(timer);
  }, [compareNotice]);

  const activePrograms = programs[tab] || [];
  const comparePrograms = programs[comparePick] || [];

  const TESTIMONIAL_IDS = useMemo(
    () => ({
      undergrad: ["ivana-radonjic", "dalibor-popovic", "gordana-vukovic"],
      master: ["masonicic-dusan", "danil-sztnikov", "nedzad-lajka"],
    }),
    [],
  );

  const testimonialList = useMemo(() => {
    const list = alumniTestimonials?.[lang];
    return Array.isArray(list) ? list : [];
  }, [lang]);

  const testimonialsByTab = useMemo(() => {
    const ids = TESTIMONIAL_IDS[tab] || [];
    const map = new Map(testimonialList.map((x) => [x.id, x]));
    return ids.map((id) => map.get(id)).filter(Boolean);
  }, [TESTIMONIAL_IDS, testimonialList, tab]);

  const discounts = useMemo(
    () =>
      [
        t("faq.odgovor8.1"),
        t("faq.odgovor8.2"),
        t("faq.odgovor8.3"),
        t("faq.odgovor8.4"),
      ].filter(Boolean),
    [t],
  );

  const faq = useMemo(
    () => [
      { q: t("tuitionPage.faq.q1"), a: t("tuitionPage.faq.a1") },
      { q: t("tuitionPage.faq.q2"), a: t("tuitionPage.faq.a2") },
      { q: t("tuitionPage.faq.q3"), a: t("tuitionPage.faq.a3") },
      { q: t("tuitionPage.faq.q4"), a: t("tuitionPage.faq.a4") },
      { q: t("tuitionPage.faq.q5"), a: t("tuitionPage.faq.a5") },
    ],
    [t],
  );

  const handleCompareClick = () => {
    setComparePick(tab);
    setCompareNotice({ tab, ts: Date.now() });

    if (typeof window !== "undefined") {
      const el = document.getElementById("tuition-compare");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  const justCompared =
    Boolean(compareNotice) &&
    compareNotice.tab === tab &&
    Date.now() - compareNotice.ts < 2600;

  return (
    <>
      <Navbar />

      <Bredcom
        title={t("common.home", "Početna")}
        subtitle={t(
          "pages.cijene-studija",
          t("admission.Skolarina", "Školarina"),
        )}
      />

      <div className="container mt-4 pt-40 tuition-page">
        {/* HERO */}
        <section
          className="tuition-hero"
          aria-label={t("tuitionPage.hero.aria")}
        >
          <div className="tuition-hero__content">
            <div className="tuition-eyebrow">
              <span className="tuition-pill">
                <i className="fas fa-wallet" aria-hidden="true" />
                {t("tuitionPage.hero.eyebrow")}
              </span>
            </div>

            <h2 className="tuition-title">{t("tuitionPage.hero.title")}</h2>
            <p className="tuition-subtitle">{t("tuitionPage.hero.lead")}</p>

            <div className="tuition-hero__cta">
              <a
                className="btn tuition-btn tuition-btn--primary"
                href={APPLY_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("skolarina.cta")}{" "}
                <i className="fas fa-arrow-right" aria-hidden="true" />
              </a>

              <Link className="btn tuition-btn tuition-btn--ghost" to="/upis">
                {t("tuitionPage.cta.admissions", "Info o upisu")}{" "}
                <i className="fas fa-info-circle" aria-hidden="true" />
              </Link>

              <Link
                className="btn tuition-btn tuition-btn--ghost"
                to="/kontakt"
              >
                {t("tuitionPage.cta.contact", "Kontakt")}{" "}
                <i className="fas fa-envelope" aria-hidden="true" />
              </Link>
            </div>

            <div
              className="tuition-quickfacts"
              aria-label={t("tuitionPage.quickfacts.aria")}
            >
              <div className="tuition-qf">
                <i className="fas fa-book" aria-hidden="true" />
                <span>{t("admissionsPage.quickfacts.textbooks")}</span>
              </div>
              <div className="tuition-qf">
                <i className="fas fa-laptop" aria-hidden="true" />
                <span>{t("admissionsPage.quickfacts.platform")}</span>
              </div>
              <div className="tuition-qf">
                <i className="fas fa-briefcase" aria-hidden="true" />
                <span>{t("admissionsPage.quickfacts.practice")}</span>
              </div>
              <div className="tuition-qf">
                <i className="fas fa-globe-europe" aria-hidden="true" />
                <span>{t("admissionsPage.quickfacts.erasmus")}</span>
              </div>
            </div>
          </div>

          <aside className="tuition-hero__aside">
            <div className="tuition-asideCard">
              <h3>{t("tuitionPage.aside.title")}</h3>
              <p>{t("tuitionPage.aside.text")}</p>

              <div
                className="tuition-asideLinks"
                aria-label={t("tuitionPage.aside.aria")}
              >
                <a href="#programi">
                  <i className="fas fa-th-large" aria-hidden="true" />
                  {t("tuitionPage.nav.programs")}
                </a>
                <a href="#ukljuceno">
                  <i className="fas fa-check-circle" aria-hidden="true" />
                  {t("tuitionPage.nav.included")}
                </a>
                <a href="#popusti">
                  <i className="fas fa-tags" aria-hidden="true" />
                  {t("tuitionPage.nav.discounts")}
                </a>
                <a href="#erasmus">
                  <i className="fas fa-plane-departure" aria-hidden="true" />
                  {t("tuitionPage.nav.erasmus")}
                </a>
              </div>
            </div>
          </aside>
        </section>

        {/* STICKY NAV */}
        <nav className="tuition-nav" aria-label={t("tuitionPage.nav.aria")}>
          <a href="#programi">{t("tuitionPage.nav.programs")}</a>
          <a href="#ukljuceno">{t("tuitionPage.nav.included")}</a>
          <a href="#popusti">{t("tuitionPage.nav.discounts")}</a>
          <a href="#erasmus">{t("tuitionPage.nav.erasmus")}</a>
          <a href="#dom">{t("tuitionPage.nav.dorm")}</a>
          <a href="#utisci">{t("tuitionPage.nav.testimonials")}</a>
          <a href="#praksa">{t("tuitionPage.nav.practice")}</a>
          <a href="#pitanja">{t("tuitionPage.nav.faq")}</a>
          <a href="#kontakt">{t("tuitionPage.nav.contact")}</a>
        </nav>

        {/* PROGRAMS */}
        <section id="programi" className="tuition-section">
          <header className="tuition-section__header">
            <h3 className="tuition-section__title">
              {t("tuitionPage.sections.programsTitle")}
            </h3>
            <p className="tuition-section__lead">
              {t("tuitionPage.sections.programsLead")}
            </p>

            <div
              className="tuition-tabs"
              role="tablist"
              aria-label={t("tuitionPage.tabs.aria")}
            >
              <button
                type="button"
                className={`tuition-tab ${tab === "undergrad" ? "is-active" : ""}`}
                onClick={() => setTab("undergrad")}
                role="tab"
                aria-selected={tab === "undergrad"}
              >
                {t("tuitionPage.tabs.undergrad")}
              </button>
              <button
                type="button"
                className={`tuition-tab ${tab === "master" ? "is-active" : ""}`}
                onClick={() => setTab("master")}
                role="tab"
                aria-selected={tab === "master"}
              >
                {t("tuitionPage.tabs.master")}
              </button>
            </div>

            <div className="tuition-specialistNotice" role="note">
              <div>
                <span>{t("tuitionPage.specialistNotice.kicker")}</span>
                <h4>{t("tuitionPage.specialistNotice.title")}</h4>
                <p>{t("tuitionPage.specialistNotice.lead")}</p>
              </div>
              <div className="tuition-specialistNotice__actions">
                <Link to="/specijalisticke-studije">
                  {t("tuitionPage.specialistNotice.ctaNotice")}
                  <i className="fas fa-arrow-right" aria-hidden="true" />
                </Link>
                <Link to="/poslovna-ekonomija">
                  {t("specialistNotice.businessEconomics")}
                  <i className="fas fa-arrow-right" aria-hidden="true" />
                </Link>
                <Link to="/ekonomija-javnog-sektora">
                  {t("specialistNotice.publicEconomics")}
                  <i className="fas fa-arrow-right" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </header>

          <div className="tuition-grid">
            {activePrograms.map((p) => (
              <TuitionCard
                key={p.key}
                p={p}
                t={t}
                commonPoints={commonPoints}
                justCompared={justCompared}
                onCompare={handleCompareClick}
              />
            ))}
          </div>

          {/* Compare table (desktop) */}
          <div className="tuition-desktopOnly">
            <div id="tuition-compare" className="tuition-compareAnchor" />
            {justCompared ? (
              <div
                className="tuition-compareToast"
                role="status"
                aria-live="polite"
              >
                <span className="tuition-compareToast__icon" aria-hidden="true">
                  ✓
                </span>
                <span>
                  {t(
                    "tuitionPage.compare.toast",
                    "Upoređeno! Tabela je ispod.",
                  )}
                </span>
              </div>
            ) : null}
            <CompareTable programs={comparePrograms} t={t} />
          </div>
        </section>

        {/* INCLUDED */}
        <section id="ukljuceno" className="tuition-section">
          <header className="tuition-section__header">
            <h3 className="tuition-section__title">
              {t("tuitionPage.sections.includedTitle")}
            </h3>
            <p className="tuition-section__lead">
              {t("tuitionPage.sections.includedLead")}
            </p>
          </header>

          <div className="tuition-includes">
            <div className="tuition-include">
              <i className="fas fa-book" aria-hidden="true" />
              <h4>{t("tuitionPage.includes.textbooksTitle")}</h4>
              <p>{t("skolarina.benefit1")}</p>
            </div>

            <div className="tuition-include">
              <i className="fas fa-laptop" aria-hidden="true" />
              <h4>{t("tuitionPage.includes.platformTitle")}</h4>
              <p>{t("skolarina.benefit2")}</p>
            </div>

            <div className="tuition-include">
              <i className="fas fa-user-check" aria-hidden="true" />
              <h4>{t("tuitionPage.includes.supportTitle")}</h4>
              <p>{t("skolarina.benefit3")}</p>
            </div>

            <div className="tuition-include">
              <i className="fas fa-chalkboard-teacher" aria-hidden="true" />
              <h4>{t("tuitionPage.includes.materialsTitle")}</h4>
              <p>{t("skolarina.benefit4")}</p>
            </div>

            <div className="tuition-include">
              <i className="fas fa-plane-departure" aria-hidden="true" />
              <h4>{t("tuitionPage.includes.erasmusTitle")}</h4>
              <p>{t("tuitionPage.includes.erasmusText")}</p>
            </div>

            <div className="tuition-include">
              <i className="fas fa-briefcase" aria-hidden="true" />
              <h4>{t("tuitionPage.includes.practiceTitle")}</h4>
              <p>{t("tuitionPage.includes.practiceText")}</p>
            </div>
          </div>

          <div className="tuition-ctaRow">
            <Link className="btn tuition-btn tuition-btn--ghost" to="/erasmus">
              {t("tuitionPage.cta.erasmus")}{" "}
              <i className="fas fa-globe-europe" aria-hidden="true" />
            </Link>

            <a
              className="btn tuition-btn tuition-btn--ghost"
              href={PRACTICE_PDF}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("tuitionPage.cta.practicePdf")}{" "}
              <i className="fas fa-file-pdf" aria-hidden="true" />
            </a>

            <Link
              className="btn tuition-btn tuition-btn--primary"
              to="/kontakt"
            >
              {t("tuitionPage.cta.contact", "Kontakt")}{" "}
              <i className="fas fa-envelope" aria-hidden="true" />
            </Link>
          </div>
        </section>

        {/* DISCOUNTS */}
        <section id="popusti" className="tuition-section">
          <header className="tuition-section__header">
            <h3 className="tuition-section__title">
              {t("tuitionPage.discounts.title")}
            </h3>
            <p className="tuition-section__lead">
              {t("tuitionPage.discounts.lead")}
            </p>
          </header>

          <div className="tuition-discounts">
            <ul>
              {discounts.map((x) => (
                <li key={x}>
                  <i className="fas fa-tag" aria-hidden="true" />
                  <span>{x}</span>
                </li>
              ))}
            </ul>

            <div className="tuition-discounts__note">
              <i className="fas fa-info-circle" aria-hidden="true" />{" "}
              {t("tuitionPage.discounts.note")}
            </div>
          </div>
        </section>

        {/* ERASMUS */}
        <section id="erasmus" className="tuition-section">
          <div className="tuition-split">
            <div className="tuition-split__main">
              <h3 className="tuition-section__title">
                {t("tuitionPage.erasmus.title")}
              </h3>
              <p className="tuition-section__lead">
                {t("tuitionPage.erasmus.lead")}
              </p>

              <div className="tuition-ctaRow">
                <Link
                  className="btn tuition-btn tuition-btn--primary"
                  to="/erasmus"
                >
                  {t("tuitionPage.erasmus.ctaMain")}{" "}
                  <i className="fas fa-globe-europe" aria-hidden="true" />
                </Link>
                <Link
                  className="btn tuition-btn tuition-btn--ghost"
                  to="/erasmus-konkursi"
                >
                  {t("tuitionPage.erasmus.ctaCalls")}{" "}
                  <i className="fas fa-bullhorn" aria-hidden="true" />
                </Link>
              </div>
            </div>

            <aside className="tuition-split__aside">
              <div className="tuition-noteCard">
                <h4>{t("tuitionPage.erasmus.boxTitle")}</h4>
                <p>{t("tuitionPage.erasmus.boxText")}</p>
              </div>
            </aside>
          </div>
        </section>

        {/* DORM */}
        <section id="dom" className="tuition-section">
          <div className="tuition-split">
            <div className="tuition-split__main">
              <h3 className="tuition-section__title">
                {t("tuitionPage.dorm.title")}
              </h3>
              <p className="tuition-section__lead">
                {t("tuitionPage.dorm.lead")}
              </p>

              <div className="tuition-ctaRow">
                <Link
                  className="btn tuition-btn tuition-btn--primary"
                  to="/dom-studenata"
                >
                  {t("tuitionPage.dorm.cta")}{" "}
                  <i className="fas fa-bed" aria-hidden="true" />
                </Link>
                <Link
                  className="btn tuition-btn tuition-btn--ghost"
                  to="/kontakt"
                >
                  {t("tuitionPage.cta.contact", "Kontakt")}{" "}
                  <i className="fas fa-envelope" aria-hidden="true" />
                </Link>
              </div>
            </div>

            <aside className="tuition-split__aside">
              <div className="tuition-noteCard tuition-noteCard--gold">
                <h4>{t("tuitionPage.dorm.boxTitle")}</h4>
                <p>{t("tuitionPage.dorm.boxText")}</p>
              </div>
            </aside>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section id="utisci" className="tuition-section">
          <header className="tuition-section__header">
            <h3 className="tuition-section__title">
              {t("tuitionPage.testimonials.title")}
            </h3>
            <p className="tuition-section__lead">
              {t("tuitionPage.testimonials.lead")}
            </p>
          </header>

          <div className="tuition-testimonials">
            {testimonialsByTab.map((x) => (
              <article key={x.id} className="tuition-testimonial">
                <div className="tuition-testimonial__top">
                  <Avatar name={x.name} src={x.image} />
                  <div className="tuition-testimonial__meta">
                    <div className="tuition-testimonial__name">{x.name}</div>
                    <div className="tuition-testimonial__role">
                      {x.position}
                    </div>
                    <div className="tuition-testimonial__small">
                      {x.level} • {x.year}
                    </div>
                  </div>
                </div>

                <h4 className="tuition-testimonial__title">{x.quoteTitle}</h4>
                <p className="tuition-testimonial__quote">{x.quote}</p>
              </article>
            ))}
          </div>

          <div className="tuition-ctaRow">
            <Link
              className="btn tuition-btn tuition-btn--ghost"
              to="/alumni-klub"
            >
              {t("tuitionPage.testimonials.cta")}{" "}
              <i className="fas fa-users" aria-hidden="true" />
            </Link>
          </div>
        </section>

        {/* PRACTICE MODEL */}
        <section id="praksa" className="tuition-section">
          <div className="tuition-split">
            <div className="tuition-split__main">
              <h3 className="tuition-section__title">
                {t("tuitionPage.practice.title")}
              </h3>
              <p className="tuition-section__lead">
                {t("tuitionPage.practice.lead")}
              </p>

              <ul className="tuition-steps">
                <li>
                  <strong>1.</strong> {t("tuitionPage.practice.s1")}
                </li>
                <li>
                  <strong>2.</strong> {t("tuitionPage.practice.s2")}
                </li>
                <li>
                  <strong>3.</strong> {t("tuitionPage.practice.s3")}
                </li>
              </ul>

              <div className="tuition-ctaRow">
                <Link
                  className="btn tuition-btn tuition-btn--primary"
                  to="/model-studentske-prakse"
                >
                  {t("tuitionPage.practice.ctaPage")}{" "}
                  <i className="fas fa-arrow-right" aria-hidden="true" />
                </Link>

                <a
                  className="btn tuition-btn tuition-btn--ghost"
                  href={PRACTICE_PDF}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("tuitionPage.practice.ctaPdf")}{" "}
                  <i className="fas fa-file-pdf" aria-hidden="true" />
                </a>
              </div>
            </div>

            <aside className="tuition-split__aside">
              <div className="tuition-noteCard">
                <h4>{t("tuitionPage.practice.boxTitle")}</h4>
                <p>{t("tuitionPage.practice.boxText")}</p>
              </div>
            </aside>
          </div>
        </section>

        {/* FAQ */}
        <section id="pitanja" className="tuition-section">
          <header className="tuition-section__header">
            <h3 className="tuition-section__title">
              {t("tuitionPage.faq.title")}
            </h3>
            <p className="tuition-section__lead">{t("tuitionPage.faq.lead")}</p>
          </header>

          <div className="tuition-faq">
            {faq.map((x) => (
              <FaqItem key={x.q} q={x.q} a={x.a} />
            ))}
          </div>
        </section>

        {/* CONTACT / FINAL CTA */}
        <section id="kontakt" className="tuition-section">
          <div className="tuition-final">
            <div className="tuition-final__copy">
              <h3 className="tuition-final__title">
                {t("tuitionPage.final.title")}
              </h3>
              <p className="tuition-final__lead">
                {t("tuitionPage.final.lead")}
              </p>
            </div>

            <div className="tuition-final__actions">
              <a
                className="btn tuition-btn tuition-btn--primary"
                href={APPLY_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("skolarina.cta")}{" "}
                <i className="fas fa-arrow-right" aria-hidden="true" />
              </a>

              <Link
                className="btn tuition-btn tuition-btn--ghost"
                to="/kontakt"
              >
                {t("tuitionPage.cta.contact", "Kontakt")}{" "}
                <i className="fas fa-envelope" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
}

export default CijeneStudija;
